import React, { useState, useEffect } from "react";
import { adminCreateSubAdmin, adminUpdateSubAdmin } from "../api/admin";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const modules = ["Dashboard", "Users", "Products", "Variants", "Categories", "Subcategories", "Orders", "Banners", "Blogs", "Shipping", "Wallet", "Contacts", "Settings"];

const SubAdminForm = ({ subadmin, onSuccess, onCancel }) => {
    const [name, setName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [role, setRole] = useState("subadmin");
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        if (subadmin) {
            setName(subadmin.name ?? "");
            setUsername(subadmin.username ?? "");
            setPassword(""); // Password should be empty when editing unless changing
            setRole(subadmin.role ?? "subadmin");
            setPermissions(subadmin.permissions || []);
        } else {
            setName("");
            setUsername("");
            setPassword("");
            setRole("subadmin");
            setPermissions([]);
        }
    }, [subadmin]);

    const handlePermissionChange = (moduleName, type) => {
        const perm = `${moduleName}:${type}`;
        setPermissions(prev => {
            if (prev.includes(perm)) {
                return prev.filter(p => p !== perm);
            } else {
                // If adding write, also add read
                if (type === "write" && !prev.includes(`${moduleName}:read`)) {
                    return [...prev, perm, `${moduleName}:read`];
                }
                return [...prev, perm];
            }
        });
    };

    const hasPerm = (moduleName, type) => permissions.includes(`${moduleName}:${type}`);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim() || !username.trim() || (!subadmin && !password.trim())) return;

        const data = {
            name,
            username,
            role,
            permissions,
        };

        if (password.trim()) {
            data.password = password;
        }

        try {
            if (subadmin) {
                const subadminId = subadmin._id || subadmin.id;
                await adminUpdateSubAdmin(subadminId, data);
                toast.success("Sub-Admin updated successfully! ✨");
            } else {
                await adminCreateSubAdmin(data);
                toast.success("Sub-Admin created successfully! 🛡️");
            }
            onSuccess();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to save sub-admin. ❌");
        }
    };

    return (
        <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-body">
                <div className="form-grid">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full Name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Username</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Username for login"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Password {subadmin && "(Leave empty to keep current)"}</label>
                        <div style={{ position: "relative", width: "100%" }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required={!subadmin}
                                style={{ paddingRight: "40px", width: "100%", boxSizing: "border-box" }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#666",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Role</label>
                        <select className="role-select" value={role} onChange={(e) => setRole(e.target.value)}>
                            <option value="subadmin">Sub-Admin</option>
                        </select>
                    </div>
                </div>

                <div className="permissions-section" style={{ marginTop: "20px" }}>
                    <h4 style={{ marginBottom: "10px", borderBottom: "1px solid #eee", paddingBottom: "5px" }}>Module Permissions</h4>
                    <div className="permissions-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                        {modules.map(module => (
                            <div key={module} className="permission-item" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px", background: "#f9f9f9", borderRadius: "4px" }}>
                                <span style={{ fontWeight: "500" }}>{module}</span>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <input 
                                            type="checkbox" 
                                            checked={hasPerm(module, "read")} 
                                            onChange={() => handlePermissionChange(module, "read")}
                                        /> Read
                                    </label>
                                    <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <input 
                                            type="checkbox" 
                                            checked={hasPerm(module, "write")} 
                                            onChange={() => handlePermissionChange(module, "write")}
                                        /> Read-Write
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="form-footer" style={{ marginTop: "30px" }}>
                    <button type="submit" className="btn-submit">
                        {subadmin ? "Update Sub-Admin" : "Create Sub-Admin"}
                    </button>
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </form>
    );
};

export default SubAdminForm;
